import React from 'react';
import { useTranslation } from 'react-i18next';
import { TextField } from '@material-ui/core';
import { useField } from 'formik';
import { FormikProps, FormikValues, useFormikContext } from 'formik';

import useStudentsFormKey from '../../../hooks/use-students-form-key';
import { StudentFormEntry } from 'components/ClassroomsModule/types';
import { get } from 'lodash';
import { StudentFieldProps } from '../../../types';

interface StudentUsernameFieldProps extends StudentFieldProps {
  usernames?: Array<string>;
}

export const StudentUsernameField: React.FC<StudentUsernameFieldProps> = ({
  index,
  touched,
  usernames
}) => {
  const { t } = useTranslation('AddStudentsStep');
  const [usernameGenerated, setUsernamGenerated] = React.useState(false);
  const { values }: FormikProps<FormikValues> = useFormikContext();
  const studentsFormKey = useStudentsFormKey();
  const students = get(values, studentsFormKey);
  const name = `${studentsFormKey}.${index}.username`;
  const [firstNameField] = useField({ name: `${studentsFormKey}.${index}.firstName` });
  const [lastNameField] = useField({ name: `${studentsFormKey}.${index}.lastName` });
  const [usernameField, meta, helpers] = useField({ name });
  const currentStudent = students[index];

  React.useEffect(() => {
    if (firstNameField.value && lastNameField.value && !usernameField.value && !usernameGenerated) {
      setUsernamGenerated(true);
      const initialUsername = `${firstNameField.value} ${lastNameField.value.charAt(0)}`;
      const generateUsername = (username: string, i = 0): string => {
        const testUsername = i === 0 ? username : `${username}${i}`;
        const found =
          students.find(
            (student: StudentFormEntry) =>
              testUsername.toLowerCase() === student?.username?.toLowerCase()
          ) || usernames?.find((user: string) => testUsername.toLowerCase() === user.toLowerCase());
        if (!found) {
          return testUsername;
        }
        return generateUsername(username, i + 1);
      };
      const value = generateUsername(initialUsername);
      helpers.setValue(value);
    }
  }, [
    firstNameField.value,
    lastNameField.value,
    usernameField.value,
    helpers,
    usernames,
    setUsernamGenerated,
    usernameGenerated,
    currentStudent,
    students
  ]);

  return (
    <TextField
      {...usernameField}
      key={`${usernameField.name}_input`}
      fullWidth
      placeholder={t('Username')}
      variant="outlined"
      size="small"
      value={usernameField.value || ''}
      error={Boolean((touched || meta.touched) && meta.error)}
      helperText={(touched || meta.touched) && meta.error}
      inputProps={{
        'aria-label': `${t('Student')} ${index + 1} ${t('Username')}`
      }}
    />
  );
};

export default StudentUsernameField;
